import type { ActionOption, PublishedAttribute } from '~/dashboard/views/workflows/store/engine/types';
import { ActionType } from '../../constants';
import { generatePublicId } from '~/dashboard/views/workflows/store/engine/utils';
import type { CreateInputAttributeActionPayload, CreateOutputAttributeActionPayload } from './types';


export function buildIndexableActionOptionEntry(
  name: string, args: Partial<ActionOption>,
): { [name: string]: ActionOption } {
  return {
    [name]: {
      jsonValue: null,
      publicId: generatePublicId('actionoption'),
      value: null,
      serviceConfigOptionId: null,
      selectedValues: null,
      ...args,
    },
  };
}

/**
 * Indexes entries into a single object
 * ie: turns [{name1: object1}, {name2: object2}] into {name1: object1, name2: object2}
 * @param entries array of entries to index into `record`
 * @param record optional record with existing data to index into
 * @returns indexed entries into `record` (if passed) or into an new record
 */
export function indexEntriesIntoRecord<T extends any>(
  entries: { [k: string]: T }[],
  record: Record<string, T> = {},
): Record<string, T> {
  return entries.reduce((indexedRecord, entry) => {
    const [key, value] = Object.entries(entry)[0];
    indexedRecord[key] = value;
    return indexedRecord;
  }, record);
}

export function buildCreateActionPayloadForPublishedAttribute({
  attributeData,
  actionType,
  outputOpts,
}: {
  attributeData: PublishedAttribute;
  actionType: ActionType;
  outputOpts: {
    category?: string;
  };
}): CreateOutputAttributeActionPayload | CreateInputAttributeActionPayload {
  const basePayloadArgs = {
    name: attributeData.attributeName,
    publishedAttributeToken: attributeData.attributeToken,
    dataType: attributeData.dataType,
    isPublished: true,
    jqFunction: attributeData.jqFilter ?? '',
    publishedDetails: attributeData,
    // While the `cra-config-pas` flag is being implemented and rolled out, we are saving the category
    // in the outputOpts with the flag off and in the attributeData with the flag on (since it will be saved on the PA with the flag on)
    // Once this flag is fully rolled out, we can remove the outputOpts
    ...(outputOpts?.category && outputOpts.category.trim() && {
      category: outputOpts.category,
    }),
    ...(attributeData.category && attributeData.category.trim() && {
      category: attributeData.category,
    }),
    ...(attributeData.valueRangeConfig?.length > 0 && {
      outputValueRange: attributeData.valueRangeConfig,
    }),
  };
  if (actionType === ActionType.OUTPUT) {
    return {
      type: actionType,
      args: {
        ...basePayloadArgs,
        // we want the jq values to be empty when creating a new output, even if it's a PA
        useJq: 'false',
        jqFunction: '',
        publishedDetails: attributeData,
      },
    };
  }
  if (actionType === ActionType.INPUT) {
    return {
      type: actionType,
      args: {
        ...basePayloadArgs,
        useJq: String(attributeData.hasJqFilter),
        publishedDetails: attributeData,
      },
    };
  }
  return null;
}
